import { AppButton, AppTextField, AppTextArea } from 'components';
import ESTDropDownType1 from 'components/molecules/ESTDropDownType1';
import { useEffect, useState } from 'react';
import Joi from 'joi';
import { checkForErrors } from 'utilities/fns';
import ESDropDown from 'components/molecules/ESDropDown';
import ESClassDropDown from 'components/molecules/ESClassDropDown';
import CoursesMultiSelect from 'components/molecules/CoursesMultiSelect';
import PlanRatesSpecifier from 'components/molecules/PlanRatesSpecifier';
import StreamsDropDown from 'components/molecules/StreamsDropDownType';

function ManageCourseBundles({ save, selectedItem = {}, handleSelectedItem }) {
  const [courseInfo, setCourseInfo] = useState(Object.assign({}, initialState));
  const [formErrors, setFormErrors] = useState([]);

  function handleInfoChange(data = {}) {
    setCourseInfo((x) => ({ ...x, ...data }));
  }

  useEffect(() => {
    if (selectedItem?._id) {
      const { classId, es, est, name, courses, plansWithRespectiveRates } = selectedItem;
      const __courses = courses?.map?.((x) => ({ value: x._id, label: x.courseName }));
      setCourseInfo({
        classId,
        es,
        est,
        name,
        courses: __courses,
        plans: plansWithRespectiveRates,
      });
    }
  }, [selectedItem]);

  function handleSave() {
    setFormErrors([]);

    const data = {
      ...courseInfo,
      status: 'active',
      type: selectedItem?._id ? 'UPDATE' : undefined,
    };

    const validation = managePlanSchema.validate(data, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    save?.({ ...data, id: selectedItem?._id });

    clearData();
  }

  function clearData() {
    setFormErrors([]);
    setCourseInfo(Object.assign({}, initialState));
    handleSelectedItem({});
  }

  const errors = checkForErrors(formErrors);

  return (
    <>
      <div className="row">
        <div className="col-6">
          <h2 className="prime-head p-0 boldtitle">Manage Course Bundles</h2>
          {selectedItem?._id ? (
            <span>Update Course Bundle</span>
          ) : (
            <span>Create a Course Bundle</span>
          )}
        </div>
        <div className="col-6 prime-head text-right"></div>
      </div>

      <div className="row my-4">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <ESTDropDownType1
            enableAPI
            value={courseInfo.est}
            handleChange={(event) => {
              const est = event.target.value;
              handleInfoChange({ est });
            }}
          />
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <ESDropDown
            enableAPI={!!courseInfo.est}
            dependencies={{
              query: courseInfo.est ? `est=${courseInfo.est}` : ``,
            }}
            value={courseInfo.es}
            handleChange={(event) => {
              const es = event.target.value;
              handleInfoChange({ es });
            }}
            defaultLook={false}
          />
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <StreamsDropDown
            enableAPI={true}
            // dependencies={{
            //   // query: courseInfo.est ? `est=${courseInfo.est}` : ``,
            // }}
            value={courseInfo.stream}
            handleChange={(event) => {
              const stream = event.target.value;
              handleInfoChange({ stream });
            }}
            defaultLook={false}
          />
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <ESClassDropDown
            enableAPI={!!courseInfo.stream}
            dependencies={{
              query: `streamId=${courseInfo.stream}`,
            }}
            value={courseInfo.classId}
            handleChange={(event) => {
              const classId = event.target.value;
              handleInfoChange({ classId });
            }}
            defaultLook={false}
          />
        </div>
      </div>

      <div className="row my-4">
        <div className="col-lg-4 col-md-4 col-sm-12">
          <AppTextField
            labelProps={{
              label: 'Bundle Name *',
              htmlFor: 'bundleName',
            }}
            inputProps={{
              placeholder: 'Bundle Name *',
              id: 'bundleName',
              name: 'bundleName',
              value: courseInfo.name,
              onChange: (event) => {
                const name = event.target.value;
                handleInfoChange({ name });
              },
            }}
            fieldErrors={errors['name']}
          />
        </div>
        <div className="col-lg-4 col-md-4 col-sm-12">
          <CoursesMultiSelect
            enableAPI={!!courseInfo.es && !!courseInfo.est && !!courseInfo.classId}
            dependencies={{
              query: { esId: courseInfo.es, estId: courseInfo.est, classId: courseInfo.classId },
            }}
            values={courseInfo.courses}
            handleChange={(courses) => {
              handleInfoChange({ courses });
            }}
          />
        </div>
        <div className="col-lg-4 col-md-4 col-sm-12">
          <AppTextArea
            label="Course Bundle Overview"
            areaProps={{
              placeholder: 'Course Bundle Overview',
              value: courseInfo.courseOverview,
              onChange: (event) => {
                const courseOverview = event.target.value;
                handleInfoChange({ courseOverview });
              },
            }}
          />
        </div>
      </div>

      <div className="row">
        <PlanRatesSpecifier
          planRates={courseInfo.plans}
          handleChanges={(obj = {}) => {
            let plans = [];
            // check if planId in obj is already in courseInfo.plans
            // if found then update the price value; else add;
            const planExits = courseInfo.plans.some((plan) => plan.planId === obj.planId);
            if (planExits) {
              plans = courseInfo.plans.map((plan) => {
                if (plan.planId === obj.planId) plan.price = isNaN(obj.price) ? null : obj.price;
                return plan;
              });
              handleInfoChange({ plans: plans.filter((x) => x.price) });

              return;
            }
            plans = courseInfo.plans.concat(obj);
            handleInfoChange({ plans: plans.filter((x) => x.price) });
          }}
        />
      </div>

      <div className="d-flex flex-row justify-content-center mt-4">
        <div className="d-flex">
          <AppButton
            variantClass="btn-primary mx-3"
            style={{ minWidth: '1.5rem' }}
            onClick={handleSave}
          >
            Save
          </AppButton>
          <AppButton variantClass="btn-plane p--tint mx-3" onClick={clearData}>
            Cancel
          </AppButton>
        </div>
      </div>
    </>
  );
}

const managePlanSchema = Joi.object({
  name: Joi.string().required().trim(),
  est: Joi.string().required(),
  es: Joi.string().required(),
  classId: Joi.string().required(),
  status: Joi.string().required(),
  courses: Joi.array().required(),
});

ManageCourseBundles.defaultProps = {
  save: () => {},
  handleSelectedItem: () => {},
};

export default ManageCourseBundles;

const initialState = {
  name: '',
  classId: '',
  est: '',
  es: '',
  plans: [],
  status: 'active',
  courses: [],
};
